"use client";

import { FormEvent, useCallback, useEffect, useMemo } from "react";
import { FieldValues, Path, UseFormRegister } from "react-hook-form";

import { VALIDATION_MESSAGE } from "constants/validation";
import { cn } from "utils/cn";

const Textarea = <T extends FieldValues>({
  name,
  register,
  required,
  placeholder,
  isDisabled = false,
  isError = false,
  height = "h-[100px]",
  maxRows,
  watchedValue,
  defaultHeight,
  maxLength,
  shouldTrimData = false,
  dataTestId,
}: {
  name: Path<T>;
  register: UseFormRegister<T>;
  required: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isError?: boolean;
  height?: string;
  maxRows?: number;
  watchedValue?: string;
  defaultHeight?: string;
  maxLength?: number;
  shouldTrimData?: boolean;
  dataTestId?: string;
}) => {
  const validation = useMemo(() => {
    const baseOptions = {
      required: { value: false, message: VALIDATION_MESSAGE.REQUIRED },
    };

    return required
      ? maxLength
        ? {
            ...baseOptions,
            required: { value: true, message: VALIDATION_MESSAGE.REQUIRED },
            maxLength: { value: maxLength, message: VALIDATION_MESSAGE.TOO_LARGE_STRING(maxLength) },
          }
        : {
            ...baseOptions,
            required: { value: true, message: VALIDATION_MESSAGE.REQUIRED },
          }
      : maxLength
      ? {
          ...baseOptions,
          maxLength: { value: maxLength, message: VALIDATION_MESSAGE.TOO_LARGE_STRING(maxLength) },
        }
      : undefined;
  }, [maxLength, required]);

  const getColors = () => {
    if (isDisabled) {
      return "bg-form-disabledBackground border-input-normal";
    } else if (isError) {
      return "bg-form-isErrorBackGround border-attention";
    } else {
      return "bg-input-normal border-input-normal";
    }
  };

  const handleInput = (e: FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    const text = target.value;
    const lines = text.split(/\r*\n/);

    if (maxRows && lines.length > 1 && lines.length < maxRows) {
      target.style.height = "inherit";
      target.style.height = `${target.scrollHeight}px`;
    }
    if (defaultHeight && maxRows && lines.length === 1) {
      target.style.height = "inherit";
      target.style.height = defaultHeight;
    }
  };

  const resetHeight = useCallback(() => {
    const element = document.getElementById(name);
    if (element && !!defaultHeight) {
      element.style.height = "inherit";
      element.style.height = defaultHeight;
    }
  }, [defaultHeight, name]);

  // watchedValueがある場合は、Textareaがリセットされた時に、高さを元に戻す
  useEffect(() => {
    if (watchedValue !== undefined && watchedValue === "") {
      resetHeight();
    }
  }, [watchedValue, resetHeight]);

  const { onBlur: registerOnBlur, ...registerProps } = register(name, validation);

  return (
    <textarea
      {...registerProps}
      onInput={handleInput}
      id={name}
      data-testid={dataTestId}
      className={cn(
        "w-full rounded border px-4 py-3 placeholder:text-input-placeholder focus:border-link focus:bg-link-light focus:shadow-input focus:outline-none focus:ring-link",
        height,
        getColors()
      )}
      placeholder={placeholder}
      disabled={isDisabled}
      onBlur={(e) => {
        if (shouldTrimData) {
          e.target.value = e.target.value.trim();
        }

        registerOnBlur(e);
      }}
    />
  );
};

export default Textarea;
